import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";

import CommentInput from "components/Comments/CommentInput";
import ContextCommentPicker from "components/Comments/ContextCommentPicker";
import {
  useContextCommentsListQuery,
  useCreateOrLinkContextCommentMutation,
  useUnlinkContextCommentMutation,
} from "hooks/queries/useContextCommentsQuery";
import type { ContextCommentKind } from "lib/api/contextComments";
import type { PrivacyLevel } from "util/userUtils";
import { transformHtmlContent } from "util/transformHtmlContent";
import { getTaskUiStrings, resolveTaskLocale } from "i18n/taskUi";

export interface ContextCommentsSectionProps {
  postId: number;
  kind: ContextCommentKind;
  /** Parent post privacy; new comments inherit it. */
  parentPrivacy?: PrivacyLevel | string | null;
  /** Enables picker + create/unlink controls. */
  editable?: boolean;
  className?: string;
}

const normalizePrivacy = (value: PrivacyLevel | string | null | undefined): PrivacyLevel => {
  if (
    value === "public" ||
    value === "follow" ||
    value === "members" ||
    value === "teams" ||
    value === "besteas" ||
    value === "me" ||
    value === "admin" ||
    value === "anonymous"
  ) {
    return value;
  }
  if (value === "private") {
    return "me";
  }
  if (value === "followers") {
    return "follow";
  }
  return "public";
};

const isEmptyHtml = (html: string): boolean => {
  const trimmed = html.trim();
  return trimmed.length === 0 || trimmed === "<p></p>";
};

/**
 * Inline display and edit surface for context comments attached to a post:
 * principles on solutions and tasks on actions.
 */
const ContextCommentsSection: React.FC<ContextCommentsSectionProps> = ({
  postId,
  kind,
  parentPrivacy = "public",
  editable = false,
  className = "",
}) => {
  const labels = useMemo(() => getTaskUiStrings(resolveTaskLocale()), []);
  const [draftSeed, setDraftSeed] = useState<string | null>(null);
  const privacy = normalizePrivacy(parentPrivacy);
  const createOrLinkMutation = useCreateOrLinkContextCommentMutation(postId, kind);
  const unlinkMutation = useUnlinkContextCommentMutation(postId, kind);
  const listQuery = useContextCommentsListQuery(postId, kind);
  const items = listQuery.data?.items ?? [];

  const title = kind === "principle" ? labels.attachedPrinciplesTitle : labels.attachedTasksTitle;
  const subtitle = kind === "principle" ? labels.attachedPrinciplesSubtitle : labels.attachedTasksSubtitle;
  const empty = kind === "principle" ? labels.attachedPrinciplesEmpty : labels.attachedTasksEmpty;
  const kindLabel = kind === "principle" ? "principle" : "task";

  const handleExistingPick = async (commentId: number) => {
    await createOrLinkMutation.mutateAsync({ existing_comment_id: commentId });
  };

  const handleUnlink = async (commentId: number) => {
    const confirmMessage = labels.unlinkConfirm.replace("{kind}", kindLabel);
    if (!window.confirm(confirmMessage)) {
      return;
    }
    try {
      await unlinkMutation.mutateAsync({ commentId });
    } catch {
      toast.error(labels.attachedSaveError);
    }
  };

  return (
    <section className={`mt-3 rounded-lg border border-eoe-border-main p-3 dark:border-eoe-dark-border-main ${className}`}>
      <div className="mb-2">
        <div className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">{title}</div>
        <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">{subtitle}</p>
      </div>

      {listQuery.isLoading ? (
        <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">{labels.loading}</div>
      ) : null}

      {!listQuery.isLoading && items.length === 0 ? (
        <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">{empty}</div>
      ) : null}

      {items.length > 0 ? (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="rounded-md bg-eoe-bg-hover p-2 text-sm text-eoe-text dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1 leading-relaxed">{transformHtmlContent(item.content || "")}</div>
                {editable ? (
                  <button
                    type="button"
                    onClick={() => handleUnlink(Number(item.id))}
                    className="rounded-full p-1 text-eoe-text-sub hover:text-warning-red dark:text-eoe-dark-text-sub"
                    aria-label={labels.unlink}
                    disabled={unlinkMutation.isPending}
                  >
                    <X className="size-4" aria-hidden />
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {editable ? (
        <div className="mt-3 space-y-2">
          <ContextCommentPicker
            kind={kind}
            ownerScope="me"
            onPick={(comment) => {
              void handleExistingPick(Number(comment.id));
            }}
            onCreateNew={(seed) => setDraftSeed(seed.content)}
            excludeCommentIds={items.map((item) => item.id)}
          />
          {draftSeed !== null ? (
            <div className="rounded-md border border-eoe-border-main p-2 dark:border-eoe-dark-border-main">
              <CommentInput
                action="add"
                buttonText={kind === "principle" ? labels.addPrinciple : labels.addTask}
                initialContent={draftSeed.trim().length > 0 ? `<p>${draftSeed.trim()}</p>` : ""}
                forcedPrivacy={privacy === "anonymous" ? "public" : privacy}
                showPrivacy={false}
                showTimeline={false}
                showPlace={false}
                allowCoauthors={false}
                onSubmit={async (content) => {
                  if (isEmptyHtml(content)) {
                    return;
                  }
                  try {
                    await createOrLinkMutation.mutateAsync({
                      content,
                      privacy: privacy === "anonymous" ? "public" : privacy,
                    });
                    setDraftSeed(null);
                  } catch {
                    toast.error(labels.attachedSaveError);
                  }
                }}
                onCancel={() => setDraftSeed(null)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default ContextCommentsSection;
